/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useResizeVal } from 'src/modules/labeling/contexts/ResizeContext';
import { Label } from 'src/modules/labeling/types/Label';
import { Rect } from 'src/modules/labeling/types/Rect';
import { getTokenElementByIndex } from 'src/modules/labeling/utils/layout';
import { RenderPhase, RenderPhaseAction } from 'src/modules/labeling/utils/renderPhaseReducer';

export type TokenRectsInput<T> = {
    /**
     * The absolute start token index of the line relative
     * to the other lines.
     */
    absoluteStartTokenIndex: number;

    /**
     * Array of lines of utterance tokens.
     */
    tokens: string[];

    /**
     * Tokens container element ref.
     */
    containerRef: React.MutableRefObject<T>;

    /**
     * Array of labels.
     */
    labels: Label[];

    /**
     * When toggled, we need to recalculate layout.
     */
    hideNames: boolean;

    /**
     * Which state in the rendering are we in.
     */
    renderPhase: RenderPhase;

    /**
     * Dispatches against the rendering phase reducer.
     */
    dispatchRenderPhase: (action: RenderPhaseAction) => void;
};

/**
 * Returns an array of memoized functions when called each returns the bounding rect
 * of the token at the specified index.
 */
export const useTokenRects = <T extends HTMLElement>(input: TokenRectsInput<T>) => {
    const { containerRef, tokens, hideNames, labels, renderPhase, dispatchRenderPhase, absoluteStartTokenIndex } = input;
    const resizeVal = useResizeVal();
    const [tokenRects, setTokenRects] = React.useState<Rect[]>([]);

    const callback = React.useCallback(() => {
        if (!containerRef.current) {
            return;
        }

        const rects: Rect[] = [];
        const { left: parentLeft, right: parentRight, top: parentTop } = containerRef.current.getBoundingClientRect();

        for (let index = 0; index < tokens.length; index++) {
            const tokenElement = getTokenElementByIndex(containerRef.current, index + absoluteStartTokenIndex);
            const { left, right, top, width, height } = tokenElement.getBoundingClientRect();

            rects.push({
                start: left - parentLeft,
                end: parentRight - right,
                top: Math.max(top - parentTop, 0),
                height,
                width
            });
        }

        unstable_batchedUpdates(() => {
            setTokenRects(rects);
        });
    }, [tokens]);

    // Recalculates token bounds.
    React.useEffect(() => {
        if (renderPhase === 'ready') {
            return;
        }

        callback();
        dispatchRenderPhase({ actionType: 'next' });
    }, [renderPhase]);

    // Layout effect that runs whenever the labeler is resized.
    React.useLayoutEffect(() => {
        dispatchRenderPhase({ actionType: 'invalidate' });
    }, [resizeVal]);

    // Layout effect that runs whenever the padding of the tokens
    // change due to either hiding labels names or changing
    // the labels themselves.
    React.useLayoutEffect(() => {
        dispatchRenderPhase({ actionType: 'invalidatePadding' });
    }, [hideNames, labels]);

    return tokenRects;
};
